import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

export function NeedleCursor() {
  const [visible, setVisible] = useState(false);
  const [interactive, setInteractive] = useState(false);
  const [pressed, setPressed] = useState(false);

  const enabled = useRef(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);

  const smoothX = useSpring(x, {
    stiffness: 700,
    damping: 45,
    mass: 0.18,
  });

  const smoothY = useSpring(y, {
    stiffness: 700,
    damping: 45,
    mass: 0.18,
  });

  useEffect(() => {
    const finePointer = window.matchMedia(
      "(hover: hover) and (pointer: fine)"
    );

    enabled.current = finePointer.matches;

    if (!enabled.current) return;

    const handleMove = (event) => {
      x.set(event.clientX);
      y.set(event.clientY);

      setVisible(true);

      const target = event.target;

      if (!(target instanceof Element)) {
        setInteractive(false);
        return;
      }

      const clickable = target.closest(
        [
          "a",
          "button",
          ".btn",
          ".text-link",
          ".round-link",
          ".card",
          ".kss-orange-hover",
          "[role='button']",
          "input",
          "select",
          "textarea",
        ].join(",")
      );

      setInteractive(Boolean(clickable));
    };

    const handleDown = () => {
      setPressed(true);
    };

    const handleUp = () => {
      setPressed(false);
    };

    const handleLeave = () => {
      setVisible(false);
    };

    const handleEnter = () => {
      setVisible(true);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mousedown", handleDown);
    window.addEventListener("mouseup", handleUp);

    document.documentElement.addEventListener(
      "mouseleave",
      handleLeave
    );

    document.documentElement.addEventListener(
      "mouseenter",
      handleEnter
    );

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mousedown", handleDown);
      window.removeEventListener("mouseup", handleUp);

      document.documentElement.removeEventListener(
        "mouseleave",
        handleLeave
      );

      document.documentElement.removeEventListener(
        "mouseenter",
        handleEnter
      );
    };
  }, [x, y]);

  return (
    <motion.div
      className={[
        "kss-needle-cursor",
        visible ? "is-visible" : "",
        interactive ? "is-interactive" : "",
        pressed ? "is-pressed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        x: smoothX,
        y: smoothY,
      }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 32 52"
        xmlns="http://www.w3.org/2000/svg"
        focusable="false"
      >
        {/* Orange thread */}
        <path
          className="kss-needle-thread"
          d="
            M 11 10
            C 4 4, 1 10, 5 15
            C 8 19, 14 17, 15 13
          "
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />

        {/* Needle eye */}
        <ellipse
          className="kss-needle-eye"
          cx="12"
          cy="12"
          rx="3.2"
          ry="4.6"
          fill="none"
          strokeWidth="1.5"
        />

        {/* Needle body */}
        <path
          className="kss-needle-body"
          d="M14 16 L25 43"
          fill="none"
          strokeWidth="1.7"
          strokeLinecap="round"
        />

        {/* Needle tip */}
        <path
          className="kss-needle-tip"
          d="M23.5 40.5 L27 49 L26.5 39.5 Z"
        />

        {/* Tiny highlight */}
        <path
          className="kss-needle-highlight"
          d="M15.2 18 L23.2 38"
          fill="none"
          strokeWidth="0.65"
          strokeLinecap="round"
        />
      </svg>
    </motion.div>
  );
}